import { BROWSER_BATCH_SIZE } from "../../../../config"
import Logger from "../../logger"
import { Decision, FeatureFlagDecision, RemoteConfigDecision } from "../../model/model"
import CollectionUtil from "../../util/CollectionUtil"
import { isCounterMetric } from "../Counter"
import { FlushMetricRegistry } from "../flush/FlushMetricRegistry"
import { Metric } from "../Metric"
import { Metrics } from "../Metrics"
import { isTimerMetric, TimerSample } from "../Timer"
import { Scheduler } from "../../scheduler/Scheduler"
import { Lifecycle } from "../../lifecycle/Lifecycle"
import { LifecycleChangeListener } from "../../lifecycle/LifecycleChangeListener"
import { MonitoringMetricDispatcher } from "./MonitoringMetricDispatcher"

export class MonitoringMetricRegistry extends FlushMetricRegistry implements LifecycleChangeListener {
  constructor(
    private readonly dispatcher: MonitoringMetricDispatcher,
    scheduler: Scheduler,
    pushIntervalMillis: number
  ) {
    super(scheduler, pushIntervalMillis)
    this.start()
  }

  protected flushMetrics(metrics: Metric[], sync: boolean): void {
    CollectionUtil.chunked(metrics.filter(this.isDispatchTarget.bind(this)), BROWSER_BATCH_SIZE).forEach(
      (targetMetrics) => {
        this.dispatch(targetMetrics, sync)
      }
    )
  }

  // Dispatch only measured metrics
  private isDispatchTarget(metric: Metric): boolean {
    if (isCounterMetric(metric) || isTimerMetric(metric)) return metric.count() > 0

    return false
  }

  private dispatch(metrics: Metric[], sync: boolean) {
    this.dispatcher.dispatch(metrics, sync).catch((e) => Logger.log.debug(`Failed to flushing metrics: ${e}`))
  }

  onLifecycleChanged(visibility: Lifecycle, _: number): void {
    switch (visibility) {
      case "hidden":
      case "pagehide":
        return this.publish(true)
      case "visible":
      case "pageshow":
      case "blur":
      case "locationChange":
      case "focus":
        return
    }
  }
}

export class DecisionMetrics {
  static experiment(sample: TimerSample, key: number, decision: Decision) {
    const timer = Metrics.timer("experiment.decision", {
      key: String(key),
      variation: decision.variation,
      reason: String(decision.reason)
    })
    sample.stop(timer)
  }

  static featureFlag(sample: TimerSample, key: number, decision: FeatureFlagDecision) {
    const timer = Metrics.timer("feature.flag.decision", {
      key: String(key),
      on: String(decision.isOn),
      reason: String(decision.reason)
    })
    sample.stop(timer)
  }

  static remoteConfig(sample: TimerSample, key: string, decision: RemoteConfigDecision) {
    const timer = Metrics.timer("remote.config.decision", {
      key: String(key),
      reason: String(decision.reason)
    })
    sample.stop(timer)
  }
}
